// Core
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

// Components
import Box from "components/Atoms/Box";
import Button from "components/Atoms/Button";
import Input from "components/Atoms/Input";
import Typography from "components/Atoms/Typography/TypographyV1";
import CoverLayout from "components/LayoutContainers/CoverLayout";
import Logo from "components/Logo";

// Images
import curved11 from "assets/images/curved-images/curved11.jpg";

// Redux
import { useSelector } from "react-redux";
import { selectThemeMode } from "state/application/applicationSlice";
import { useChangePasswordMutation } from "state/auth/authApiSlice";

function PasswordReset() {
  const { t } = useTranslation(["auth"]);
  const navigate = useNavigate();
  const { token } = useParams();
  const themeMode = useSelector(selectThemeMode);
  // eslint-disable-next-line no-unused-vars
  const isDark = themeMode === "dark";

  const [password, setPassword] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(undefined);
  const [changePassword] = useChangePasswordMutation();

  // eslint-disable-next-line consistent-return
  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (password !== confirmPassword) {
      return setError(t("reset_password.errors.mismatch"));
    }
    try {
      await changePassword({ password, token }).unwrap();
      setPasswordChanged(true);
      // Set a timeout for 2 seconds before navigating to the login page.
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(t("login.errors.generic"));
    }
  };

  return (
    <CoverLayout
      image={curved11}
      header={
        <Box mb={2}>
          <Box mb={2}>
            <Logo size="medium" showSubtitle />
          </Box>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              color: "#10b981",
              background: "linear-gradient(195deg, #10b981, #059669)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("reset.title")}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="regular"
            sx={{ color: isDark ? "#cbd5e0" : "#718096" }}
          >
            {t("reset_password.subtitle")}
          </Typography>
        </Box>
      }
    >
      <Box component="form" role="form">
        <Box mb={2} lineHeight={1.25}>
          <Box mb={1} ml={0.5}>
            <Typography component="label" variant="caption" fontWeight="bold">
              {t("reset_password.new_password_label")}
            </Typography>
          </Box>
          <Input
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            placeholder={t("reset_password.new_password_label")}
            required
          />
        </Box>
        <Box mb={2} lineHeight={1.25}>
          <Box mb={1} ml={0.5}>
            <Typography component="label" variant="caption" fontWeight="bold">
              {t("reset_password.confirm_password_label")}
            </Typography>
          </Box>
          <Input
            onChange={(event) => setConfirmPassword(event.target.value)}
            type="password"
            placeholder={t("reset_password.confirm_password_label")}
            required
          />
        </Box>
        <Box mt={2} mb={2} textAlign="center">
          <h6
            style={{
              fontSize: ".7em",
              color: "red",
              textAlign: "center",
              fontWeight: 400,
              transition: ".2s all",
            }}
          >
            {error}
          </h6>
        </Box>
        <Box mt={4} mb={1}>
          <Button
            onClick={handleSubmit}
            variant="gradient"
            buttonColor={passwordChanged ? "success" : "info"}
            disabled={passwordChanged}
            fullWidth
          >
            {passwordChanged ? t("reset_password.success") : t("reset_password.submit")}
          </Button>
        </Box>
      </Box>
    </CoverLayout>
  );
}

export default PasswordReset;
